/* @jsx h */
import { defineComponent, h, ref, PropType } from "vue";
import { RightSheet, ResponsiveTable } from "@lemmonhq/uikit";
import type { ResponsiveTableColumn } from "@lemmonhq/uikit";
import type { TaxProfile, TaxItemType } from "@lemmonhq/types";
import { useToast } from "@app/vue/providers/Toast";
import {
  taxSettingsAPI,
  type CreateTaxProfileData,
  type UpdateTaxProfileData,
} from "@app/data/api/tax_settings";
import { defaultProfileForm, type ProfileFormState } from "./utils";

export const ProfilesTab = defineComponent({
  name: "ProfilesTab",
  props: {
    profiles: { type: Array as PropType<TaxProfile[]>, required: true },
    taxItemTypes: { type: Array as PropType<TaxItemType[]>, required: true },
    onRefresh: {
      type: Function as PropType<() => Promise<void>>,
      required: true,
    },
  },
  setup(props) {
    const toast = useToast();
    const isSheetOpen = ref(false);
    const selectedProfile = ref<TaxProfile | null>(null);
    const mode = ref<"create" | "edit" | "view">("create");
    const form = ref<ProfileFormState>(defaultProfileForm());
    const submitting = ref(false);

    const openCreate = () => {
      mode.value = "create";
      selectedProfile.value = null;
      form.value = defaultProfileForm();
      isSheetOpen.value = true;
    };

    const openSheet = (profile: TaxProfile) => {
      selectedProfile.value = profile;
      if (!profile.is_editable) {
        mode.value = "view";
      } else {
        mode.value = "edit";
        form.value = {
          tax_name: profile.tax_name,
          service_method: profile.service_method ?? "",
          product_item_type: profile.product_item_type ?? "",
          jurisdiction: profile.jurisdiction ?? "",
        };
      }
      isSheetOpen.value = true;
    };

    const closeSheet = () => {
      isSheetOpen.value = false;
      selectedProfile.value = null;
    };

    const handleCreate = async (event: Event) => {
      event.preventDefault();
      if (submitting.value) return;

      if (!form.value.tax_name.trim()) {
        toast.showToast("Give the profile a tax name before saving.", {
          type: "warning",
        });
        return;
      }

      const payload: CreateTaxProfileData = {
        tax_name: form.value.tax_name.trim(),
        service_method: form.value.service_method.trim() || undefined,
        product_item_type: form.value.product_item_type.trim() || undefined,
        jurisdiction: form.value.jurisdiction.trim() || undefined,
      };

      try {
        submitting.value = true;
        await taxSettingsAPI.createTaxProfile(payload);
        await props.onRefresh();
        toast.showToast("Tax profile created.", { type: "success" });
        closeSheet();
      } catch (error) {
        console.error("Failed to create tax profile", error);
        toast.showToast(
          "We could not create that profile. Try again after checking the fields.",
          { type: "error" },
        );
      } finally {
        submitting.value = false;
      }
    };

    const handleUpdate = async (event: Event) => {
      event.preventDefault();
      if (!selectedProfile.value || submitting.value) return;

      const payload: UpdateTaxProfileData = {
        tax_name: form.value.tax_name.trim() || undefined,
        service_method: form.value.service_method.trim() || null,
        product_item_type: form.value.product_item_type.trim() || null,
        jurisdiction: form.value.jurisdiction.trim() || null,
      };

      try {
        submitting.value = true;
        await taxSettingsAPI.updateTaxProfile(selectedProfile.value.id, payload);
        await props.onRefresh();
        toast.showToast("Tax profile updated.", { type: "success" });
        closeSheet();
      } catch (error) {
        console.error("Failed to update tax profile", error);
        toast.showToast(
          "We could not update that profile. Check the fields and try again.",
          { type: "error" },
        );
      } finally {
        submitting.value = false;
      }
    };

    const handleDelete = async () => {
      if (!selectedProfile.value || submitting.value) return;
      try {
        submitting.value = true;
        await taxSettingsAPI.deleteTaxProfile(selectedProfile.value.id);
        await props.onRefresh();
        toast.showToast("Tax profile deleted.", { type: "success" });
        closeSheet();
      } catch (error) {
        console.error("Failed to delete tax profile", error);
        toast.showToast("Deleting that profile failed. Please try again.", {
          type: "error",
        });
      } finally {
        submitting.value = false;
      }
    };

    const profileFormFields = () => (
      <div class="space-y-4">
        <label class="form-control w-full">
          <span class="label-text text-sm font-medium text-base-content/70">
            Tax name
          </span>
          <input
            class="input input-bordered"
            placeholder="Standard Swiss VAT"
            value={form.value.tax_name}
            onInput={(e) =>
              (form.value.tax_name = (e.target as HTMLInputElement).value)
            }
          />
        </label>

        <div class="grid gap-4 md:grid-cols-2">
          <label class="form-control w-full">
            <span class="label-text text-sm font-medium text-base-content/70">
              Service method
            </span>
            <select
              class="select select-bordered"
              value={form.value.service_method}
              onChange={(e) =>
                (form.value.service_method = (
                  e.target as HTMLSelectElement
                ).value)
              }
            >
              <option value="">All service methods</option>
              <option value="dine-in">Dine-in</option>
              <option value="takeaway">Takeaway</option>
              <option value="delivery">Delivery</option>
            </select>
          </label>
          <label class="form-control w-full">
            <span class="label-text text-sm font-medium text-base-content/70">
              Item type
            </span>
            <select
              class="select select-bordered"
              value={form.value.product_item_type}
              onChange={(e) =>
                (form.value.product_item_type = (
                  e.target as HTMLSelectElement
                ).value)
              }
            >
              <option value="">All products</option>
              {props.taxItemTypes.map((itemType) => (
                <option value={itemType.name} key={itemType.id}>
                  {itemType.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label class="form-control w-full">
          <span class="label-text text-sm font-medium text-base-content/70">
            Jurisdiction
          </span>
          <input
            class="input input-bordered"
            placeholder="Optional"
            value={form.value.jurisdiction}
            onInput={(e) =>
              (form.value.jurisdiction = (e.target as HTMLInputElement).value)
            }
          />
        </label>
      </div>
    );

    const columns: ResponsiveTableColumn[] = [
      {
        key: "d-name",
        label: "Tax name",
        icon: "badge",
        mobilePriority: "hidden",
        render: (profile: TaxProfile) => (
          <div class="font-medium text-base-content">{profile.tax_name}</div>
        ),
      },
      {
        key: "d-service",
        label: "Service",
        icon: "restaurant",
        mobilePriority: "hidden",
        render: (profile: TaxProfile) => (
          <span class="text-sm text-base-content/80">
            {profile.service_method || "All methods"}
          </span>
        ),
      },
      {
        key: "d-item-type",
        label: "Item type",
        icon: "category",
        mobilePriority: "hidden",
        render: (profile: TaxProfile) => (
          <span class="text-sm text-base-content/80">
            {profile.product_item_type || "All products"}
          </span>
        ),
      },
      {
        key: "d-rate",
        label: "Tax rate",
        icon: "price_check",
        mobilePriority: "hidden",
        render: (profile: TaxProfile) => (
          <span class="text-sm text-base-content/80">
            {profile.tax_rate
              ? `${profile.tax_rate.name} · ${Number(profile.tax_rate.value).toFixed(2)}%`
              : "Configured externally"}
          </span>
        ),
      },
      {
        key: "d-type",
        label: "Type",
        mobilePriority: "hidden",
        render: (profile: TaxProfile) => (
          <span
            class={`badge badge-sm ${profile.is_editable ? "badge-primary" : "badge-neutral"}`}
          >
            {profile.is_editable ? "Custom" : "Default"}
          </span>
        ),
      },
      {
        key: "m-name",
        label: "Name",
        desktopHide: true,
        mobilePriority: "primary",
        render: (profile: TaxProfile) => (
          <div class="min-w-0">
            <div class="truncate text-sm font-medium text-base-content">
              {profile.tax_name}
            </div>
            <div class="truncate text-xs text-base-content/60">
              {profile.service_method || "All methods"} ·{" "}
              {profile.product_item_type || "All products"}
            </div>
          </div>
        ),
      },
      {
        key: "m-type",
        label: "Type",
        desktopHide: true,
        mobilePriority: "primary",
        mobilePrimaryClass: "flex items-center shrink-0",
        render: (profile: TaxProfile) => (
          <span
            class={`badge badge-sm ${profile.is_editable ? "badge-primary" : "badge-neutral"}`}
          >
            {profile.is_editable ? "Custom" : "Default"}
          </span>
        ),
      },
      {
        key: "m-rate",
        label: "Tax rate",
        desktopHide: true,
        mobilePriority: "secondary",
        render: (profile: TaxProfile) =>
          profile.tax_rate
            ? `${profile.tax_rate.name} · ${Number(profile.tax_rate.value).toFixed(2)}%`
            : "Configured externally",
      },
      {
        key: "actions",
        label: "",
        mobilePriority: "hidden",
        headerClass: "w-px",
        cellClass: "text-right whitespace-nowrap",
        render: (profile: TaxProfile) => (
          <button
            class="btn btn-sm btn-secondary"
            onClick={(e: MouseEvent) => {
              e.stopPropagation();
              openSheet(profile);
            }}
          >
            <span class="material-symbols-outlined text-2xl">table_edit</span>
          </button>
        ),
      },
    ];

    return () => (
      <>
        <div class="flex items-center justify-end px-4 py-3 border-b border-base-content/5">
          <button class="btn btn-primary btn-sm gap-2" onClick={openCreate}>
            <span class="material-symbols-outlined text-base">add</span>
            New tax profile
          </button>
        </div>

        {props.profiles.length === 0 ? (
          <div class="flex min-h-[280px] flex-col items-center justify-center px-8 py-12 text-center">
            <div class="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-base-200">
              <span class="material-symbols-outlined text-2xl text-base-content/40">
                badge
              </span>
            </div>
            <p class="font-medium text-base-content">No custom profiles yet</p>
            <p class="mt-1 text-sm text-base-content/60">
              Create a profile to route taxes by service method, item type, or
              jurisdiction.
            </p>
          </div>
        ) : (
          <ResponsiveTable
            columns={columns}
            rows={props.profiles}
            rowKey={(profile: TaxProfile) => profile.id}
            onRowClick={openSheet}
            emptyMessage="No tax profiles found"
          />
        )}

        <RightSheet
          isOpen={isSheetOpen.value}
          title={
            mode.value === "create"
              ? "New tax profile"
              : mode.value === "edit"
                ? "Edit tax profile"
                : selectedProfile.value?.tax_name ?? "Tax profile"
          }
          onClose={closeSheet}
          width="520px"
        >
          {mode.value === "create" && (
            <form class="flex h-full flex-col" onSubmit={handleCreate}>
              <div class="flex-1 space-y-6 overflow-y-auto px-6 py-6">
                <p class="text-sm text-base-content/70">
                  Profiles connect tax rates to specific service flows or item
                  types. Leave a field blank to keep the rule broad.
                </p>
                {profileFormFields()}
              </div>
              <div class="border-t border-base-200 bg-base-200/50 px-6 py-4 flex items-center justify-between">
                <p class="text-sm text-base-content/60">
                  Profiles apply instantly to new orders.
                </p>
                <div class="flex gap-2">
                  <button
                    type="button"
                    class="btn btn-ghost btn-sm"
                    onClick={closeSheet}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    class="btn btn-primary btn-sm"
                    disabled={submitting.value}
                  >
                    {submitting.value ? "Saving…" : "Save profile"}
                  </button>
                </div>
              </div>
            </form>
          )}

          {mode.value === "edit" && (
            <form class="flex h-full flex-col" onSubmit={handleUpdate}>
              <div class="flex-1 space-y-6 overflow-y-auto px-6 py-6">
                <p class="text-sm text-base-content/70">
                  Adjust how this tax profile links to service flows and item
                  types. Changes affect new orders instantly.
                </p>
                {profileFormFields()}
              </div>
              <div class="border-t border-base-200 bg-base-200/50 px-6 py-4 flex flex-wrap items-center justify-between gap-3">
                <button
                  type="button"
                  class="btn btn-ghost btn-sm text-error"
                  onClick={handleDelete}
                  disabled={submitting.value}
                >
                  Delete profile
                </button>
                <div class="flex gap-2">
                  <button
                    type="button"
                    class="btn btn-ghost btn-sm"
                    onClick={closeSheet}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    class="btn btn-primary btn-sm"
                    disabled={submitting.value}
                  >
                    {submitting.value ? "Saving…" : "Save changes"}
                  </button>
                </div>
              </div>
            </form>
          )}

          {mode.value === "view" && selectedProfile.value && (
            <div class="flex h-full flex-col">
              <div class="flex-1 space-y-4 overflow-y-auto px-6 py-6">
                <p class="text-sm text-base-content/70">
                  This profile is provided by Lemmon to keep your taxes
                  compliant out of the box. Duplicate it to tailor a
                  restaurant-specific version.
                </p>
                <section class="rounded-2xl border  bg-base-100 p-5 shadow-sm space-y-3">
                  {(
                    [
                      [
                        "Service method",
                        selectedProfile.value.service_method ||
                          "All service methods",
                      ],
                      [
                        "Item type",
                        selectedProfile.value.product_item_type ||
                          "All product types",
                      ],
                      [
                        "Tax rate",
                        selectedProfile.value.tax_rate
                          ? `${selectedProfile.value.tax_rate.name} · ${Number(selectedProfile.value.tax_rate.value).toFixed(2)}%`
                          : selectedProfile.value.tax_name,
                      ],
                      [
                        "Jurisdiction",
                        selectedProfile.value.jurisdiction ||
                          "All jurisdictions",
                      ],
                    ] as [string, string][]
                  ).map(([label, value]) => (
                    <div key={label}>
                      <h3 class="text-xs uppercase tracking-wide text-base-content/60">
                        {label}
                      </h3>
                      <p class="text-base text-base-content">{value}</p>
                    </div>
                  ))}
                </section>
              </div>
              <div class="border-t border-base-200 bg-base-200/50 px-6 py-4 flex items-center justify-end">
                <button
                  type="button"
                  class="btn btn-ghost btn-sm"
                  onClick={closeSheet}
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </RightSheet>
      </>
    );
  },
});
